import { useEffect, useState } from "react";
import axios from "axios";
import DeleteModal from "./DeleteModal";
import { api_url } from "../config/config";

const API_BASE =api_url;

const BlogManagement = ({ onBlogTypesChange }) => {
    const [blogTypes, setBlogTypes] = useState([]);
    const [typeInput, setTypeInput] = useState("");
    const [editingTypeId, setEditingTypeId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedType, setSelectedType] = useState(null);

    // Fetch all blog types on mount
    useEffect(() => {
        fetchBlogTypes();
    }, []);

    const fetchBlogTypes = async () => {
        try {
            const response = await axios.get(`${API_BASE}/blog-type`);
            const types = response.data?.data || [];
            setBlogTypes(types);
            onBlogTypesChange?.(types);
        } catch (error) {
            console.error("Error fetching blog types:", error);
        }
    };

    const handleCreateOrUpdate = async () => {
        const trimmed = typeInput.trim();
        if (!trimmed) return;

        const isDuplicate = blogTypes.some(
            (type) => type.name.toLowerCase() === trimmed.toLowerCase()
        );

        if (isDuplicate && !editingTypeId) return;

        try {
            if (editingTypeId) {
                // Update existing blog type
                await axios.patch(
                    `${API_BASE}/blog-type/${editingTypeId}`,
                    { name: trimmed },
                    {
                        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
                    }
                );
                const updated = blogTypes.map((type) =>
                    type.blog_type_id === editingTypeId ? { ...type, name: trimmed } : type
                );
                setBlogTypes(updated);
                onBlogTypesChange?.(updated);
            } else {
                // Create new blog type
                const response = await axios.post(
                    `${API_BASE}/blog-type`,
                    { name: trimmed },
                    {
                        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
                    }
                );
                const newType = response.data?.data;
                const updated = [...blogTypes, newType];
                setBlogTypes(updated);
                onBlogTypesChange?.(updated);
            }
            setTypeInput(""); // Clear input after saving
            setEditingTypeId(null); // Clear editing state
        } catch (error) {
            console.error("Error saving blog type:", error);
        }
    };

    const handleEditClick = (type) => {
        setEditingTypeId(type.blog_type_id);  // Now using blog_type_id for editing
        setTypeInput(type.name);
    };

    const handleDeleteClick = (type) => {
        setSelectedType(type);
        setShowModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedType) return;
        try {
            // Using blog_type_id for deletion
            await axios.delete(`${API_BASE}/blog-type/${selectedType.blog_type_id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
            });
            const updated = blogTypes.filter((t) => t.blog_type_id !== selectedType.blog_type_id);
            setBlogTypes(updated);
            onBlogTypesChange?.(updated);
        } catch (error) {
            console.error("Error deleting blog type:", error);
        } finally {
            setShowModal(false);
            setSelectedType(null);
        }
    };

    const cancelEdit = () => {
        setEditingTypeId(null);
        setTypeInput("");
    };

    return (
        <div className="p-6 bg-gray-100 min-h-screen font-pop">
            <h2 className="text-2xl font-semibold mb-4">Blog Type Management</h2>

            {/* Input section */}
            <div className="flex gap-2 mb-6 w-1/2">
                <input
                    type="text"
                    className="flex-1 p-3 border border-gray-300 rounded-lg"
                    placeholder="Enter blog type"
                    value={typeInput}
                    onChange={(e) => setTypeInput(e.target.value)}
                />
                {editingTypeId ? (
                    <>
                        <button
                            className="px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-semibold"
                            onClick={handleCreateOrUpdate}
                            disabled={!typeInput.trim()}
                        >
                            Update
                        </button>
                        <button
                            className="px-4 py-2 rounded-lg bg-gray-400 hover:bg-gray-500 text-white font-semibold"
                            onClick={cancelEdit}
                        >
                            Cancel
                        </button>
                    </>
                ) : (
                    <button
                        className={`px-4 py-2 rounded-lg text-white font-semibold ${
                            typeInput.trim() &&
                            !blogTypes.some(
                                (type) =>
                                    type.name.toLowerCase() ===
                                    typeInput.trim().toLowerCase()
                            )
                                ? "bg-green-500 hover:bg-green-600"
                                : "bg-gray-400 cursor-not-allowed"
                        }`}
                        onClick={handleCreateOrUpdate}
                        disabled={
                            !typeInput.trim() ||
                            blogTypes.some(
                                (type) =>
                                    type.name.toLowerCase() ===
                                    typeInput.trim().toLowerCase()
                            )
                        }
                    >
                        Create Bolg Type
                    </button>
                )}
            </div>

            {/* Blog Types list */}
            <div className="grid grid-cols-2 gap-4">
                {blogTypes.map((type) => (
                    <div
                        key={type.blog_type_id}  // Using blog_type_id here
                        className="flex justify-between items-center p-3 border border-green-500 rounded-lg bg-white shadow-md"
                    >
                        <span className="font-semibold">{type.name}</span>
                        <div className="flex gap-2">
                            <button
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 rounded-lg"
                                onClick={() => handleEditClick(type)}
                            >
                                Edit
                            </button>
                            <button
                                className="bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded-lg"
                                onClick={() => handleDeleteClick(type)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Delete Modal */}
            <DeleteModal
                showModal={showModal}
                onDelete={confirmDelete}
                onCancel={() => setShowModal(false)}
            />
        </div>
    );
};

export default BlogManagement;
